import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import moment from 'moment';
import { CommonService } from '../../../../services/common/common.service';
import { MembersDataService, MembersColumnsService } from '../../../services';
import { Member, MembersResult } from '../../../interfaces';
import { DialogFilerComponent } from '../../filer/filer.component';

@Component({
  selector: 'app-member-form',
  templateUrl: './member-form.component.html',
  styleUrls: ['./member-form.component.scss'],
  providers: [MessageService, ConfirmationService, DynamicDialogRef, DynamicDialogConfig],
})
export class MemberFormComponent implements OnInit, OnDestroy, AfterViewInit {

  public member: Member = {};
  public memberForm: FormGroup = new FormGroup({});
  public members: Member[] = [];
  public defaultParameters: {[param: string]: string | string[]} = {offset: '0', limit: '1000'};
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '1000'};
  public isLoading: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  public redirect = true;
  public disableSubmit = false;
  public enableFormAlert = true;

  private filerDialog: DynamicDialogRef = new DynamicDialogRef();

  constructor(
    private translate: TranslateService,
    public common: CommonService,
    private router: Router,
    private route: ActivatedRoute,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private dynamicDialogConfig: DynamicDialogConfig,
    private dynamicDialogRef: DynamicDialogRef,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private membersDataService: MembersDataService,
    public membersColumnsService: MembersColumnsService,
  ) {
  }

  ngOnInit(): void {
    console.log('ngOnInit', 'MemberFormComponent');
    this.route.params.subscribe((params: any) => {
      if (typeof params['id'] !== 'undefined') {
        this.titleService.setTitle('Member - Edit');
        this.getMember(parseInt(params['id'], 10));
      }
    });
    if (this.dynamicDialogConfig.data && typeof this.dynamicDialogConfig.data.member !== 'undefined') {
      this.member = this.dynamicDialogConfig.data.member;
    }
    if (typeof this.member.id === 'undefined' || this.member.id === null) {
      this.member.id = null;
      this.member.isPublished = false;
    }
    this.getMembers();
    this.prepareFrom();
  }

  ngOnDestroy(): void {
    console.log('ngOnDestroy', 'MemberFormComponent');
  }

  ngAfterViewInit(): void {
    console.log('ngAfterViewInit', 'MemberFormComponent');
  }

  getMember(id: number): void {
    this.isLoading = true;
    this.membersDataService.getMember(id).subscribe((response: Member|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.member = response;
        this.prepareFrom();
      }
    });
  }

  getMembers(): void {
    if (this.dynamicDialogConfig.data && typeof this.dynamicDialogConfig.data.members !== 'undefined' && this.dynamicDialogConfig.data.members.length > 0) {
      this.members = this.dynamicDialogConfig.data.members;
      return;
    }
    this.membersDataService.getMembers(this.parameters).subscribe((response: MembersResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.members = response.results;
      }
    });
  }

  prepareFrom(): void {
    this.memberForm = new FormGroup({
      id: new FormControl(this.member.id, []),
      name: new FormControl(this.member.name, [Validators.required, ]),
      user: new FormControl((typeof this.member.user === 'number') ? this.member.user : this.member.user?.id, [Validators.required, ]),
      description: new FormControl(this.member.description, []),
      photo: new FormControl(this.member.photo, []),
      height: new FormControl(this.member.height, []),
      weight: new FormControl(this.member.weight, []),
      isPublished: new FormControl(this.member.isPublished, [Validators.required, ]),
      fitnessGoals: new FormControl(((typeof this.member.fitnessGoals === 'undefined') ? [] : this.member.fitnessGoals as any[]).map((item: any) => { return (typeof item === 'number') ? item : item.id; }), []),
      sports: new FormControl(((typeof this.member.sports === 'undefined') ? [] : this.member.sports as any[]).map((item: any) => { return (typeof item === 'number') ? item : item.id; }), []),
    });
  }

  list() {
    this.router.navigate(['/members']);
  }

  showErrors() {
      this.showErrors();
      const invalidFields: string[] = [];
      Object.keys(this.memberForm.controls).forEach((field: any) => {
        const control = this.memberForm.get(field);
        if (control !== null) {
          control.markAsTouched({ onlySelf: true });
          if (control.invalid) {
            invalidFields.push(field);
          }
        }
      });
  }

  submitForm(redirect: boolean) {
    this.redirect = redirect;
    if (!this.memberForm.valid) {
      this.showErrors();
    } else {
      this.saveData(redirect);
    }
  }

  saveData(redirect: boolean) {
    this.member = this.memberForm.value;
    this.disableSubmit = true;
    console.log('member', this.member);
    if (typeof this.member.id !== 'undefined' && this.member.id !== null) {
      this.add(redirect);
    } else {
      this.update(redirect);
    }
    return false;
  }

  add(redirect: boolean) {
    this.membersDataService.updateMember(this.member).subscribe((data: any) => {
      this.disableSubmit = false;
      this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: this.translate.instant('MEMBERUPDATED')});
      if (redirect && typeof this.dynamicDialogConfig.data !== 'undefined') {
        this.dynamicDialogRef.close({refresh: true});
      } else if (redirect) {
        this.list();
      }
    }, (error: any) => {
      this.disableSubmit = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('MEMBERNOTUPDATED') + ' ' + error.message});
      console.warn(error);
    });
  }

  update(redirect: boolean) {
    this.membersDataService.addMember(this.member).subscribe((data: any) => {
        this.disableSubmit = false;
        this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: this.translate.instant('MEMBERCREATED')});
        if (redirect && typeof this.dynamicDialogConfig.data !== 'undefined') {
          this.dynamicDialogRef.close({refresh: true});
        } else if (redirect) {
          this.list();
        }
      }, (error: any) => {
        this.disableSubmit = false;
        this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('MEMBERNOTCREATED') + ' ' + error.message});
        console.warn(error);
    });
  }

  toggleAllFitnessGoals(event: any) {
    const control = this.memberForm.get('fitnessGoals');
    if (control !== null) {
      if (event.checked)  {
        control.setValue(this.membersColumnsService.fitnessGoalsFiltered.map((item: any) => { return item.id; }));
      } else {
        control.setValue([]);
      }
    }
  }

  toggleAllSports(event: any) {
    const control = this.memberForm.get('sports');
    if (control !== null) {
      if (event.checked)  {
        control.setValue(this.membersColumnsService.sportsFiltered.map((item: any) => { return item.id; }));
      } else {
        control.setValue([]);
      }
    }
  }

  openFilerDialog(field: string): void {
    let url = '';
    const control = this.memberForm.get(field);
    if (control !== null) {
      url = control.value;
    }
    this.filerDialog = this.dialogService.open(DialogFilerComponent, {
      header: '', // this.translate.instant('FILEMANAGER'),
      width: '90%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1500,
      data: {
        path: 'members',
        field: field,
        url: url,
      },
    });
    this.filerDialog.onClose.subscribe((response: any) => {
      if (response && response.selected) {
        const control = this.memberForm.get(field);
        if (control !== null) {
          control.setValue(response.selected.webPath);
        }
      }
    });
  }

}

@Component({
  selector: 'app-member-form',
  templateUrl: './member-form.component.html',
  styleUrls: ['./member-form.component.scss'],
  providers: [MessageService, ConfirmationService],
})
export class DialogMemberFormComponent extends MemberFormComponent {
}

